import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import {
    OPERATIONS_INCOME_WS_HANDLERS, tableValueClassName, RentAdjustmentNames,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { RentAdjustment } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './GrossRentAdjustmentsTable.scss';

export interface IGrossRentAdjustmentsTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const GrossRentAdjustmentsTable = ({ operationBeginIndex, baseCashFlowColumns }: IGrossRentAdjustmentsTableProps) => {
    const {
        incomeSummary, handleUpdateOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, operatingInfo,
    } = useProFormaSocketContext();

    const includeGainLossToLease = operatingInfo?.includeGainLossToLease;

    const handleUpdateRentAdjustment = (id: number, field: string) => handleUpdateOperationsRow(
        OPERATIONS_INCOME_WS_HANDLERS.GROSS_RENT_ADJUSTMENT.UPDATE, id, field,
    );
    const incomeSummaryAdjustments = useMemo(() => {
        if (includeGainLossToLease) {
            return incomeSummary.rentAdjustments || [];
        }
        return (incomeSummary.rentAdjustments || []).filter(
            row => row.name !== RentAdjustmentNames.GAIN_LOSS_TO_LEASE,
        );
    }, [includeGainLossToLease, incomeSummary.rentAdjustments]);

    const cashFlowColumns: ColumnDef<RentAdjustment>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `gross-rent-adjustments-period-${column.id}`,
        accessorFn: (row, index) => {
            if (!operationBeginIndex) return null;

            if (row.name === RentAdjustmentNames.CONCESSIONS) {
                if (row.amountPerYear === null) return null;
                return operationsCashFlow?.[cashFlowIndex]?.concessions || 0;
            }
            if (row.name === RentAdjustmentNames.NON_REVENUE_UNITS) {
                return operationsCashFlow?.[cashFlowIndex]?.nonRevenueUnits || 0;
            }
            if (row.name === RentAdjustmentNames.GAIN_LOSS_TO_LEASE) {
                return operationsCashFlow?.[cashFlowIndex]?.gainLossToLease || 0;
            }

            return null;
        },
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalRentalIncome || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

    const columns: ColumnDef<RentAdjustment>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <span className="px-1">Gross rent adjustments</span>
            ),
            cell: ({ row, getValue }) => (
                <span className="text-dark px-1">{getValue() as string}</span>
            ),
            footer: () => <span className="px-1">Total rental income</span>,
        },
        {
            accessorKey: 'units',
            id: 'units',
            header: () => <span>Units</span>,
            cell: ({ row, getValue }) => row.original.name === RentAdjustmentNames.NON_REVENUE_UNITS && (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateRentAdjustment(Number(row.original.id), 'units');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
        },
        {
            accessorKey: 'valuePerSf',
            id: 'valuePerSf',
            header: () => <span>$/RSF/mo.</span>,
            cell: ({ row, getValue }) => {
                if (row.original.name === RentAdjustmentNames.GAIN_LOSS_TO_LEASE) {
                    return (
                        <span className="text-muted">-</span>
                    );
                }
                return (
                    <span className={tableValueClassName(getValue() || 0)}>
                        {formatDisplayValue(getValue() || 0, true, 'currency')}
                    </span>
                );
            },
            footer: () => <span>{formatDisplayValue(incomeSummary.totalRentalIncome?.totalPricePerSf || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'valuePerUnit',
            id: 'valuePerUnit',
            header: () => <span>$/unit/mo.</span>,
            cell: ({ row, getValue }) => {
                if (row.original.name === RentAdjustmentNames.GAIN_LOSS_TO_LEASE) {
                    return (
                        <span className="text-muted">-</span>
                    );
                }
                return (
                    <span className={tableValueClassName(getValue() || 0)}>
                        {formatDisplayValue(getValue() || 0, true, 'currency')}
                    </span>
                );
            },
            footer: () => <span>{formatDisplayValue(incomeSummary.totalRentalIncome?.totalPricePerUnit || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => {
                if (row.original.name === RentAdjustmentNames.GAIN_LOSS_TO_LEASE) {
                    return (
                        <span className="text-muted">-</span>
                    );
                }

                return (
                    <span className={`${tableValueClassName(getValue() || 0)} pr-1`}>
                        {formatDisplayValue(getValue() || 0, showCents, 'currency')}
                    </span>
                );
            },
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.totalRentalIncome?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...cashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummaryAdjustments,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable GrossRentAdjustmentsTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default GrossRentAdjustmentsTable;
